import {useCallback, useMemo} from 'react'
import {useSelector} from 'react-redux'
import {buildOfferContext} from 'middleware/analytics/contexts/offerContext'
import {isVioOfferOrBundleSelected} from 'modules/accommodation/utils'
import {HotelOfferEntity, SearchOffer} from 'modules/sapiSearch/slice'
import {getCurrentOffersSearchId} from 'modules/searchApi/selectors'

import {useDispatchTeamEvent} from '@vio/core/src/analytics/components/TeamContext'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  AnalyticsContext,
  Category,
  Entity,
  HotelContext
} from '@vio/core/src/analytics/types/Events'

import {BetterValueHotel} from '../components/BetterValueCarousel/types'
import {PriceTrendContent} from '../types'
import {PriceTrendMessageKey} from '../utils/messages'
import {
  getActionType,
  getApLayout,
  getInsightType,
  getTooltipType
} from '../utils/trackingMappings'

interface UsePriceInsightsTrackingParams {
  hotelId: string
  content: PriceTrendContent | null
  buttonMessageKey: PriceTrendMessageKey | null
  offerPrice?: number | null
}

export const usePriceInsightsTracking = ({
  hotelId,
  content,
  buttonMessageKey,
  offerPrice
}: UsePriceInsightsTrackingParams) => {
  const dispatchTeamEvent = useDispatchTeamEvent()
  const searchId = useSelector(getCurrentOffersSearchId)
  const isVioOffer = isVioOfferOrBundleSelected()

  const apLayout = getApLayout(isVioOffer)
  const insightType = getInsightType(content?.recommendation.messageKey)
  const actionType = getActionType(buttonMessageKey, insightType)
  const isSimilarHotelsAvailable = content?.similarHotels !== undefined

  const priceInsightsId = `${searchId}_${hotelId}`

  const commonPayload = useMemo(
    () => ({priceInsightsId, apLayout}),
    [priceInsightsId, apLayout]
  )

  const fullPayload = useMemo(
    () => ({
      ...commonPayload,
      insightType,
      actionType,
      isSimilarHotelsAvailable
    }),
    [commonPayload, insightType, actionType, isSimilarHotelsAvailable]
  )

  const trackSucceeded = useCallback(
    (
      succeededInsightType: string | null,
      succeededActionType: string | null,
      hasSimilarHotels: boolean
    ) => {
      dispatchTeamEvent(
        trackEvent({
          category: Category.System,
          entity: Entity.PriceInsights,
          action: Action.Succeeded,
          payload: {
            ...commonPayload,
            insightType: succeededInsightType,
            actionType: succeededActionType,
            isSimilarHotelsAvailable: hasSimilarHotels
          }
        })
      )
    },
    [dispatchTeamEvent, commonPayload]
  )

  const trackDisplayed = useCallback(() => {
    dispatchTeamEvent(
      trackEvent({
        category: Category.System,
        entity: Entity.PriceInsights,
        action: Action.Displayed,
        payload: {...fullPayload}
      })
    )
  }, [dispatchTeamEvent, fullPayload])

  const trackViewed = useCallback(
    (source?: 'banner' | 'button', actionTypeOverride?: string | null) => {
      dispatchTeamEvent(
        trackEvent({
          category: Category.User,
          entity: Entity.PriceInsights,
          action: Action.Viewed,
          payload: {
            ...fullPayload,
            ...(source && {source}),
            ...(actionTypeOverride != null && {actionType: actionTypeOverride})
          }
        })
      )
    },
    [dispatchTeamEvent, fullPayload]
  )

  const trackPreviewViewed = useCallback(() => {
    dispatchTeamEvent(
      trackEvent({
        category: Category.User,
        entity: Entity.PriceInsightsPreview,
        action: Action.Viewed,
        payload: {...fullPayload}
      })
    )
  }, [dispatchTeamEvent, fullPayload])

  const trackClicked = useCallback(
    (isOpen: boolean) => {
      const expandAction = isOpen ? 'expanded' : 'collapsed'
      dispatchTeamEvent(
        trackEvent({
          category: Category.User,
          entity: Entity.PriceInsights,
          action: Action.Clicked,
          payload: {
            priceInsightsId,
            action: expandAction
          }
        })
      )
    },
    [dispatchTeamEvent, priceInsightsId]
  )

  const trackCTAClicked = useCallback(
    (actionTypeOverride?: string | null) => {
      dispatchTeamEvent(
        trackEvent({
          category: Category.User,
          entity: Entity.PriceInsightsCTA,
          action: Action.Clicked,
          payload: {
            ...fullPayload,
            ...(actionTypeOverride != null && {actionType: actionTypeOverride})
          }
        })
      )
    },
    [dispatchTeamEvent, fullPayload]
  )

  const trackTooltipClicked = useCallback(
    (infoCardName: string) => {
      dispatchTeamEvent(
        trackEvent({
          category: Category.User,
          entity: Entity.PriceInsightsTooltip,
          action: Action.Clicked,
          payload: {
            priceInsightsId,
            tooltipType: getTooltipType(infoCardName)
          }
        })
      )
    },
    [dispatchTeamEvent, priceInsightsId]
  )

  const carouselPayload = useMemo(
    () => ({priceInsightsId, anchorOfferPrice: offerPrice}),
    [priceInsightsId, offerPrice]
  )

  const trackCarouselDisplayed = useCallback(
    (hotels: BetterValueHotel[]) => {
      dispatchTeamEvent(
        trackEvent({
          category: Category.User,
          entity: Entity.BetterValueCarousel,
          action: Action.Displayed,
          payload: {
            hotelCount: hotels.length,
            carouselContext: hotels.map(hotel => ({
              hotelId: hotel.hotelId,
              offerPrice: hotel.price,
              offerId: hotel.offerId
            })),
            ...carouselPayload
          }
        })
      )
    },
    [dispatchTeamEvent, carouselPayload]
  )

  const trackCarouselScrolled = useCallback(() => {
    dispatchTeamEvent(
      trackEvent({
        category: Category.User,
        entity: Entity.BetterValueCarousel,
        action: Action.Scrolled,
        payload: {...carouselPayload}
      })
    )
  }, [dispatchTeamEvent, carouselPayload])

  const trackCarouselHotelClicked = useCallback(
    (
      hotel: BetterValueHotel,
      position: number,
      offer: SearchOffer,
      offerEntity: HotelOfferEntity
    ) => {
      dispatchTeamEvent(
        trackEvent({
          category: Category.User,
          entity: Entity.BetterValueHotelCard,
          action: Action.Clicked,
          analyticsContext: {
            [AnalyticsContext.HotelContext]: {
              hotelId: hotel.hotelId,
              cheapestPrice: hotel.price,
              hotelPosition: position,
              highestVisiblePrice: hotel.price,
              hotelTags: [],
              isHotelMapOpen: false,
              distanceFromSearchedLocation: 0,
              isPartiallyMatched: false
            } satisfies HotelContext,
            [AnalyticsContext.OfferContext]: buildOfferContext(
              offer.id,
              Entity.BetterValueCarousel,
              offerEntity,
              offer,
              1,
              false,
              null,
              null
            )
          },
          payload: {hotelId: hotel.hotelId, position, ...carouselPayload}
        })
      )
    },
    [dispatchTeamEvent, carouselPayload]
  )

  const trackCarouselSeeMoreClicked = useCallback(() => {
    dispatchTeamEvent(
      trackEvent({
        category: Category.User,
        entity: Entity.BetterValueSeeMore,
        action: Action.Clicked,
        payload: {...carouselPayload}
      })
    )
  }, [dispatchTeamEvent, carouselPayload])

  return {
    trackSucceeded,
    trackDisplayed,
    trackViewed,
    trackPreviewViewed,
    trackClicked,
    trackCTAClicked,
    trackTooltipClicked,
    trackCarouselDisplayed,
    trackCarouselScrolled,
    trackCarouselHotelClicked,
    trackCarouselSeeMoreClicked,
    priceInsightsId,
    apLayout,
    insightType,
    actionType,
    isSimilarHotelsAvailable
  }
}
